import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { studyApi } from "@/lib/api";

interface StudyMigrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMigrationSuccess?: () => void;
}

interface MigrationStats {
    studies_created: number;
    studies_skipped: number;
    total_studies: number;
    errors: string[];
    scope?: string;
    scope_message?: string;
    databases?: string[];
}

export const StudyMigrationModal: React.FC<StudyMigrationModalProps> = ({
    isOpen,
    onClose,
    onMigrationSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [testing, setTesting] = useState<boolean>(false);
    const [migrating, setMigrating] = useState<boolean>(false);
    const [connectionResult, setConnectionResult] = useState<{
        success: boolean;
        message: string;
        studies_count?: number;
    } | null>(null);
    const [stats, setStats] = useState<MigrationStats | null>(null);
    const statsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (stats && statsEndRef.current) {
            statsEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    }, [stats]);

    const activeCompanyId = useMemo<string | null>(() => {
        const stored = localStorage.getItem("vigepra_active_company");
        return stored && stored !== "main" ? stored : null;
    }, [isOpen]);

    const isMainContext = activeCompanyId === null;

    const handleTestConnection = async (): Promise<void> => {
        setTesting(true);
        setConnectionResult(null);
        try {
            const res = await studyApi.testMigration();
            const connData =
                (
                    res as {
                        data?: {
                            success: boolean;
                            message: string;
                            studies_count?: number;
                        };
                    }
                )?.data || res;
            if (connData) {
                setConnectionResult(connData);
                if (connData.success) {
                    showSuccess(t("studies.connection_ok"));
                } else {
                    showError(
                        connData.message || t("studies.connection_error")
                    );
                }
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: { message?: string; data?: { message?: string } };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                t("studies.connection_error");
            showError(errorMsg);
            setConnectionResult({
                success: false,
                message: errorMsg,
            });
        } finally {
            setTesting(false);
        }
    };

    const handleRunMigration = async (): Promise<void> => {
        setMigrating(true);
        setStats(null);
        try {
            const res = await studyApi.runMigration();
            if (res?.data) {
                setStats(res.data);
                showSuccess(t("studies.migration_success"));
                onMigrationSuccess?.();
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: {
                        message?: string;
                        errors?: Record<string, unknown>;
                    };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                (err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(" ")
                    : "") ||
                err?.message ||
                t("studies.migration_error");

            showError(errorMsg);
            setStats({
                studies_created: 0,
                studies_skipped: 0,
                total_studies: 0,
                errors: [errorMsg],
            });
        } finally {
            setMigrating(false);
        }
    };

    const handleClose = (): void => {
        if (!migrating) {
            setConnectionResult(null);
            setStats(null);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t("tools.study_migration.title")}
            onSubmit={handleRunMigration}
            submitLabel={t("tools.study_migration.run_migration")}
            cancelLabel={t("common.close")}
            isSubmitting={migrating}
            submitDisabled={migrating || testing}
            //submitIcon="database"
            maxWidth="xl"
        >
            <div className="flex flex-col items-center justify-center space-y-4">
                <div
                    className={`w-full p-4 rounded-xl border text-xs flex flex-col items-center justify-center text-center gap-2 ${
                        isMainContext
                            ? "bg-amber-50/70 border-amber-200 text-amber-900 dark:bg-amber-950/20 dark:border-amber-800/40 dark:text-amber-300"
                            : "bg-blue-50/70 border-blue-200 text-blue-900 dark:bg-blue-950/20 dark:border-blue-800/40 dark:text-blue-300"
                    }`}
                >
                    <Icon
                        value={isMainContext ? "database" : "building"}
                        size={18}
                        className="shrink-0 text-amber-700 dark:text-amber-400"
                    />
                    <div>
                        <p className="font-semibold text-center">
                            {isMainContext
                                ? t("tools.study_migration.scope_all_title")
                                : t(
                                      "tools.study_migration.scope_company_title"
                                  )}
                        </p>
                        <p className="mt-0.5 opacity-90 text-center">
                            {isMainContext
                                ? t("tools.study_migration.scope_all_desc")
                                : t("tools.study_migration.scope_company_desc")}
                        </p>
                    </div>
                </div>

                <div className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-dark-card flex flex-col items-center justify-center text-center gap-2 text-xs text-slate-600 dark:text-zinc-400">
                    <Icon
                        value="checkCircle"
                        size={18}
                        className="text-primary shrink-0"
                    />
                    <div>
                        <p className="font-medium text-slate-800 dark:text-zinc-200 text-center">
                            {t("tools.study_migration.duplicate_notice_title")}
                        </p>
                        <p className="mt-0.5 text-center">
                            {t("tools.study_migration.duplicate_notice_desc")}
                        </p>
                    </div>
                </div>

                <div className="w-full flex flex-col items-center justify-center text-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card">
                    <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                            {t("tools.study_migration.test_connection")}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {t("tools.tender_migration.ssh_config_hint")}
                        </p>
                    </div>

                    <Button
                        variant="secondary"
                        onClick={handleTestConnection}
                        disabled={testing || migrating}
                    >
                        <div className="flex items-center gap-2">
                            <Icon
                                value={testing ? "refresh" : "check"}
                                size={15}
                                className={testing ? "animate-spin" : ""}
                            />
                            <span>
                                {testing
                                    ? t(
                                          "tools.study_migration.testing_connection"
                                      )
                                    : t(
                                          "tools.study_migration.test_connection"
                                      )}
                            </span>
                        </div>
                    </Button>

                    {connectionResult && (
                        <div
                            className={`w-full p-4 rounded-xl border text-sm flex flex-col items-center justify-center text-center gap-2 ${
                                connectionResult.success
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/40 dark:text-emerald-300"
                                    : "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/40 dark:text-rose-300"
                            }`}
                        >
                            <Icon
                                value={
                                    connectionResult.success
                                        ? "checkCircle"
                                        : "alertCircle"
                                }
                                size={22}
                                className="shrink-0"
                            />
                            <div className="w-full">
                                <p className="font-semibold text-center break-words">
                                    {connectionResult.message}
                                </p>
                                {connectionResult.studies_count !==
                                    undefined && (
                                    <p className="text-xs mt-1 text-center font-medium">
                                        {t(
                                            "tools.study_migration.studies_found",
                                            {
                                                count: connectionResult.studies_count,
                                            }
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {stats && (
                    <div className="w-full space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                            <Icon
                                value="checkCircle"
                                size={16}
                                className="text-emerald-600"
                            />
                            <span className="font-semibold text-slate-900 dark:text-white">
                                {t("studies.migration_success")}
                            </span>
                        </div>

                        {stats.scope_message && (
                            <p className="text-xs text-slate-500 dark:text-zinc-400 italic">
                                {stats.scope_message}
                            </p>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.study_migration.studies_created")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                                    {stats.studies_created}
                                </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.study_migration.studies_skipped")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-blue-600 dark:text-blue-400">
                                    {stats.studies_skipped}
                                </p>
                            </div>
                        </div>

                        {stats.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-rose-600 space-y-1 text-center">
                                <p className="font-semibold">
                                    {t("tools.study_migration.errors_title")}
                                </p>
                                {stats.errors.map((err: string, i: number) => (
                                    <p key={i}>• {err}</p>
                                ))}
                            </div>
                        )}
                        <div ref={statsEndRef} />
                    </div>
                )}
            </div>
        </Modal>
    );
};
